import { JetView } from 'webix-jet';
import { devConfig, profile } from 'config/config';

/**
 * Class representing the FilterUsersVerificationView.
 * @extends JetView
 */
export default class FilterUsersVerificationView extends JetView {
    /**
     * Configures the view layout and components.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        const _ = this.app.getService('locale')._;

        // Row 1: User information input fields
        const row_1 = {
            id: 'r1',
            rows: [
                {
                    margin: 15,
                    responsive: 'r1',
                    cols: [
                        {
                            view: 'text',
                            name: 'Name',
                            minWidth: 200,
                            label: _('Nombre Completo'), // Full Name label
                            labelPosition: 'top',
                            required: true, // Field is required
                        },
                        {
                            view: 'text',
                            name: 'DocNumber',
                            minWidth: 200,
                            label: _('Cédula'), // Document Number label
                            labelPosition: 'top',
                            required: true, // Field is required
                        },
                        {
                            view: 'text',
                            name: 'Phone',
                            minWidth: 200,
                            type: 'number', // Input type is number
                            label: _('Celular'), // Phone label
                            labelPosition: 'top',
                            required: true, // Field is required
                        },
                        {
                            view: 'text',
                            name: 'Address',
                            minWidth: 200,
                            label: _('Dirección'), // Address label
                            labelPosition: 'top',
                            required: true, // Field is required
                        },
                    ],
                },
            ],
        };

        // Row 2: Social media input fields (hidden by default)
        const row_2 = {
            id: 'r2',
            hidden: true, // Initially hidden
            rows: [
                {
                    margin: 15,
                    css: 'on-row',
                    responsive: 'r2',
                    cols: [
                        {
                            view: 'text',
                            name: 'Facebook',
                            minWidth: 200,
                            label: _('Facebook'), // Facebook label
                            labelPosition: 'top',
                            required: true, // Field is required
                        },
                        {
                            view: 'text',
                            name: 'Instagram',
                            minWidth: 200,
                            label: _('Instagram'), // Instagram label
                            labelPosition: 'top',
                            required: true, // Field is required
                        },
                        {
                            view: 'richselect',
                            id: 'Other',
                            value: '1', // Default value
                            minWidth: 200,
                            label: _('Otras Redes sociales?'), // Other social media label
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            options: [
                                { id: '0', value: _('Si') }, // Yes option
                                { id: '1', value: _('No') }, // No option
                            ],
                        },
                        {
                            view: 'text',
                            name: 'OtherSocialMediaName',
                            id: 'OtherSocialMediaName',
                            minWidth: 200,
                            label: _('Nombre otra red social'), // Other social media name label
                            labelPosition: 'top',
                            hidden: true, // Initially hidden
                            required: true, // Field is required
                        },
                        {
                            view: 'text',
                            name: 'OtherSocialMediaContact',
                            id: 'OtherSocialMediaContact',
                            minWidth: 200,
                            label: _('Contacto otra red social'), // Other social media contact label
                            labelPosition: 'top',
                            hidden: true, // Initially hidden
                            required: true, // Field is required
                        },
                    ],
                },
            ],
        };

        // Row 3: Image upload and status labels
        const row_3 = {
            rows: [
                {
                    margin: 15,
                    css: 'on-row',
                    id: 'r3',
                    rows: [
                        {
                            responsive: 'r3',
                            cols: [
                                {
                                    rows: [
                                        {
                                            id: 'previewAnt',
                                            view: 'template',
                                            css: 'preview',
                                            template: '<img class="previewImage" src="#src#">', // Template for image preview
                                            data: { src: null }, // Initial data
                                            minWidth: 200,
                                            minHeight: 200,
                                        },
                                        {
                                            name: 'ImageAnt',
                                            id: 'ImageAnt',
                                            view: 'uploader',
                                            multiple: false, // Single file upload
                                            autosend: false, // Manual send
                                            css: 'previewButton',
                                            accept: 'image/png, image/jpg, image/jpeg', // Accepted file types
                                            label: _('Subir DNI Anterior'), // Upload label
                                            minWidth: 200,
                                            labelWidth: 180,
                                            on: {
                                                /**
                                                 * Event triggered after a file is added.
                                                 * @param {Object} uploadedFile - The uploaded file object.
                                                 */
                                                onAfterFileAdd: function (uploadedFile) {
                                                    if (uploadedFile.size && uploadedFile.size < 1024000) {
                                                        // Check file size
                                                        const reader = new FileReader();
                                                        reader.addEventListener(
                                                            'load',
                                                            function (event) {
                                                                const base64 = reader.result; // Get base64 result
                                                                $$('previewAnt').setValues({
                                                                    src: base64, // Set preview image
                                                                });
                                                            },
                                                            false,
                                                        );
                                                        if (uploadedFile) {
                                                            reader.readAsDataURL(uploadedFile.file); // Read file as data URL
                                                        }
                                                    } else {
                                                        webix.message({
                                                            title: _('¡Error!'), // Error title
                                                            type: 'alert-error',
                                                            text: 'El tamaño maximo permitido de las imágenes es de 1024 KB', // Error message
                                                        });
                                                    }
                                                },
                                            },
                                        },
                                        {
                                            view: 'label',
                                            align: 'center',
                                            label: _('El DNI anterior ya fue verificado.'), // Status label for previous DNI
                                            minWidth: 200,
                                            css: 'my-label-vc',
                                            id: 'DNAV',
                                            hidden: true, // Initially hidden
                                        },
                                        {
                                            view: 'label',
                                            align: 'center',
                                            label: _('El DNI anterior esta en proceso de validación.'), // Status label for previous DNI validation
                                            minWidth: 200,
                                            css: 'my-label-vc',
                                            id: 'DNAPV',
                                            hidden: true, // Initially hidden
                                        },
                                    ],
                                },
                                {
                                    rows: [
                                        {
                                            id: 'previewPos',
                                            view: 'template',
                                            css: 'preview',
                                            template: '<img class="previewImage" src="#src#">', // Template for image preview
                                            data: { src: null }, // Initial data
                                            minWidth: 200,
                                            minHeight: 200,
                                        },
                                        {
                                            name: 'ImagePos',
                                            id: 'ImagePos',
                                            view: 'uploader',
                                            multiple: false, // Single file upload
                                            autosend: false, // Manual send
                                            accept: 'image/png, image/gif, image/jpeg', // Accepted file types
                                            label: _('Subir DNI Posterior'), // Upload label
                                            css: 'previewButton',
                                            minWidth: 200,
                                            labelWidth: 180,
                                            on: {
                                                /**
                                                 * Event triggered after a file is added.
                                                 * @param {Object} uploadedFile - The uploaded file object.
                                                 */
                                                onAfterFileAdd: function (uploadedFile) {
                                                    if (uploadedFile.size && uploadedFile.size < 1024000) {
                                                        // Check file size
                                                        const reader = new FileReader();
                                                        reader.addEventListener(
                                                            'load',
                                                            function (event) {
                                                                const base64 = reader.result; // Get base64 result
                                                                $$('previewPos').setValues({
                                                                    src: base64, // Set preview image
                                                                });
                                                            },
                                                            false,
                                                        );
                                                        if (uploadedFile) {
                                                            reader.readAsDataURL(uploadedFile.file); // Read file as data URL
                                                        }
                                                    } else {
                                                        webix.message({
                                                            title: _('¡Error!'), // Error title
                                                            type: 'alert-error',
                                                            text: 'El tamaño maximo permitido de las imágenes es de 1024 KB', // Error message
                                                        });
                                                    }
                                                },
                                            },
                                        },
                                        {
                                            view: 'label',
                                            align: 'center',
                                            label: _('El DNI posterior ya fue verificado.'), // Status label for posterior DNI
                                            minWidth: 200,
                                            css: 'my-label-vc',
                                            id: 'DNPV',
                                            hidden: true, // Initially hidden
                                        },
                                        {
                                            view: 'label',
                                            align: 'center',
                                            label: _('El DNI posterior esta en proceso de validación.'), // Status label for posterior DNI validation
                                            minWidth: 200,
                                            css: 'my-label-vc',
                                            id: 'DNPPV',
                                            hidden: true, // Initially hidden
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            margin: 0,
                            rows: [
                                {
                                    view: 'label',
                                    align: 'center',
                                    label: _('Las extensiones de archivo disponibles son: JPEG, JPG, PNG'), // Supported file types
                                    minWidth: 200,
                                    css: 'my-label-vc',
                                },
                                {
                                    view: 'label',
                                    align: 'center',
                                    label: _(
                                        'En caso de no ser posible cargar las imágenes de su documento de identidad. enviar las mismas por medio del whatsapp del área comercial',
                                    ), // Instructions for image upload
                                    minWidth: 200,
                                    css: 'my-label-vc',
                                },
                                {
                                    view: 'label',
                                    align: 'center',
                                    label: _(
                                        'El tamaño sugerido de las imágenes es de 1024 KB. El proceso de verificación tarda de 12 a 48 horas hábiles',
                                    ), // Image size and verification time
                                    minWidth: 200,
                                    css: 'my-label-vc',
                                },
                            ],
                        },
                    ],
                },
            ],
        };

        // Button row: Save button and status labels
        const button = {
            id: 'r4',
            rows: [
                {
                    margin: 10,
                    responsive: 'r4',
                    cols: [
                        {
                            view: 'button',
                            id: 'button',
                            value: _('Guardar'), // Save button label
                            hotkey: 'enter', // Enter key to trigger
                            type: 'form',
                            click: () => {
                                const newdata = this.getRoot().getValues(); // Get form values
                                console.log(newdata);
                                if ($$('form').validate()) {
                                    // Validate form
                                    if (
                                        ($$('previewAnt').getValues().src != 'https://i.stack.imgur.com/lnYep.png' &&
                                            $$('previewPos').getValues().src !=
                                                'https://i.stack.imgur.com/lnYep.png') ||
                                        newdata.dniFront == 2 ||
                                        newdata.dniFrontBack == 2
                                    ) {
                                        if (newdata.dniFront == 0 || newdata.dniFront == 1) {
                                            newdata.ImageAnt = $$('previewAnt').getValues().src; // Set image for anterior DNI
                                        }
                                        if (newdata.dniFrontBack == 0 || newdata.dniFrontBack == 1) {
                                            newdata.ImagePos = $$('previewPos').getValues().src; // Set image for posterior DNI
                                        }
                                        delete newdata.dniFront; // Remove dniFront from data
                                        delete newdata.dniFrontBack; // Remove dniFrontBack from data
                                        webix
                                            .ui({
                                                view: 'window',
                                                head: {
                                                    view: 'toolbar',
                                                    cols: [
                                                        {
                                                            view: 'label',
                                                            label: _(
                                                                'Desea verificar cuenta?', // Verification prompt
                                                            ),
                                                        },
                                                    ],
                                                },
                                                position: 'center',
                                                modal: true,
                                                body: {
                                                    view: 'form',
                                                    id: 'formPass',
                                                    elements: [
                                                        {
                                                            view: 'text',
                                                            type: 'password',
                                                            label: _(
                                                                'Contraseña', // Password label
                                                            ),
                                                            name: 'Password',
                                                            required: true, // Field is required
                                                            labelWidth: 100,
                                                        },
                                                        {
                                                            cols: [
                                                                {
                                                                    view: 'button',
                                                                    value: _(
                                                                        'Cancelar', // Cancel button label
                                                                    ),
                                                                    click: function () {
                                                                        this.getTopParentView().hide(); // Hide window
                                                                    },
                                                                },
                                                                {
                                                                    view: 'button',
                                                                    type: 'form',
                                                                    value: _(
                                                                        'Guardar', // Save button label
                                                                    ),
                                                                    click: function ($e) {
                                                                        this.getTopParentView().hide(); // Hide window
                                                                        var valuesForm = $$('formPass').getValues(); // Get password input
                                                                        if (
                                                                            $$('formPass').validate() // Validate password form
                                                                        ) {
                                                                            var vthis = this;
                                                                            newdata.Password = valuesForm.Password; // Add password to data
                                                                            webix
                                                                                .ajax()
                                                                                .headers({
                                                                                    'Content-type': 'application/json', // Set content type
                                                                                })
                                                                                .post(
                                                                                    devConfig.geturlApi() +
                                                                                        'Client/verifyAccount', // API endpoint
                                                                                    newdata, // Data to send
                                                                                    {
                                                                                        error: function (
                                                                                            text,
                                                                                            data,
                                                                                            XmlHttpRequest,
                                                                                        ) {
                                                                                            vthis
                                                                                                .getTopParentView()
                                                                                                .hide(); // Hide window on error
                                                                                        },
                                                                                        success: function (
                                                                                            text,
                                                                                            data,
                                                                                            XmlHttpRequest,
                                                                                        ) {
                                                                                            var data = JSON.parse(
                                                                                                text, // Parse response
                                                                                            );
                                                                                            if (
                                                                                                !data.HasError // Check for errors
                                                                                            ) {
                                                                                                webix.alert({
                                                                                                    title: _(
                                                                                                        '¡Verificación enviada!', // Success alert title
                                                                                                    ),
                                                                                                    ok: _(
                                                                                                        'Aceptar', // Success alert button
                                                                                                    ),
                                                                                                    text: _(
                                                                                                        'Su cuenta ahora se encuentra en proceso de verificación, el área encargada validara su cuenta de 24 a 72 horas', // Success alert message
                                                                                                    ),
                                                                                                    type: 'alert-success',
                                                                                                });
                                                                                                $$('button').hide(); // Hide save button
                                                                                                $$('CPV').show(); // Show account validation in process label
                                                                                                $$('ImageAnt').hide(); // Hide anterior DNI image
                                                                                                $$('DNAPV').show(); // Show anterior DNI validation in process label
                                                                                                $$('ImagePos').hide(); // Hide posterior DNI image
                                                                                                $$('DNPPV').show(); // Show posterior DNI validation in process label
                                                                                            }
                                                                                            vthis
                                                                                                .getTopParentView()
                                                                                                .hide(); // Hide window after success
                                                                                        },
                                                                                    },
                                                                                );
                                                                        } else {
                                                                            webix.message({
                                                                                title: _(
                                                                                    '¡Error!', // Error alert title
                                                                                ),
                                                                                type: 'alert-error',
                                                                                text: _(
                                                                                    'Completa los campos...', // Error alert message
                                                                                ),
                                                                            });
                                                                        }
                                                                    },
                                                                },
                                                            ],
                                                        },
                                                    ],
                                                    elementsConfig: {
                                                        on: {
                                                            onChange: function () {
                                                                this.validate(); // Validate on change
                                                            },
                                                        },
                                                    },
                                                },
                                            })
                                            .show(); // Show confirmation window
                                    } else {
                                        webix.message({
                                            title: _('¡Error!'), // Error title
                                            type: 'alert-error',
                                            text: _('Debes subir los DNI'), // Error message
                                        });
                                    }
                                }
                            },
                        },
                        {
                            view: 'label',
                            align: 'center',
                            label: _('Su cuenta ya fue verificada.'), // Account verified label
                            minWidth: 200,
                            css: 'my-label-vc',
                            id: 'CV',
                            hidden: true, // Initially hidden
                        },
                        {
                            view: 'label',
                            align: 'center',
                            label: _('Su cuenta esta en proceso de validación.'), // Account validation in process label
                            minWidth: 200,
                            css: 'my-label-vc',
                            id: 'CPV',
                            hidden: true, // Initially hidden
                        },
                    ],
                },
            ],
        };

        // Main content with scroll view
        const content = {
            view: 'scrollview',
            scroll: 'xy',
            css: 'my-scrollview-vc',
            body: {
                rows: [row_1, row_2, row_3], // Include rows in the body
            },
        };

        // Return the complete form configuration
        return {
            view: 'form',
            id: 'form',
            rows: [content, button], // Include content and button rows
            elementsConfig: {
                on: {
                    onChange: function () {
                        this.validate(); // Validate form on change
                    },
                },
            },
        };
    }

    /**
     * Initializes the view and sets up event handlers.
     */
    init() {
        webix.ready(function () {
            $$('previewAnt').setValues({
                src: 'https://i.stack.imgur.com/lnYep.png', // Default image for anterior DNI
            });
            $$('previewPos').setValues({
                src: 'https://i.stack.imgur.com/lnYep.png', // Default image for posterior DNI
            });
            webix.ajax().get(devConfig.geturlApi() + 'Client/GetClientSpMyInfo', {
                error: function () {
                    webix.message(_('Error! ')); // Error message on AJAX failure
                },
                success: function (text) {
                    let response = JSON.parse(text); // Parse response
                    if (response[0].DNIA != undefined && response[0].DNIA != null && response[0].DNIA != '') {
                        $$('previewAnt').setValues({
                            src: response[0].DNIA, // Set anterior DNI image
                        });
                    }
                    if (response[0].DNIP != undefined && response[0].DNIP != null && response[0].DNIP != '') {
                        $$('previewPos').setValues({
                            src: response[0].DNIP, // Set posterior DNI image
                        });
                    }
                    if (window.location.href.indexOf('Agent') == -1 && response[0].BetShopType == 'V') {
                        $$('r2').show(); // Show social media row if conditions are met
                    }
                    if (response[0].dniFront == 3 && response[0].dniFrontBack == 3) {
                        $$('button').hide(); // Hide button if both DNIs are verified
                        $$('CV').show(); // Show account verified label
                    }
                    if (response[0].dniFront == 2 && response[0].dniFrontBack == 2) {
                        $$('button').hide(); // Hide button if both DNIs are in validation
                        $$('CPV').show(); // Show account validation in process label
                    }
                    if (response[0].dniFront == 2) {
                        $$('ImageAnt').hide(); // Hide anterior DNI image if in validation
                        $$('DNAPV').show(); // Show anterior DNI validation in process label
                    } else {
                        if (response[0].dniFront == 3) {
                            $$('ImageAnt').hide(); // Hide anterior DNI image if verified
                            $$('DNAV').show(); // Show anterior DNI verified label
                        }
                    }
                    if (response[0].dniFrontBack == 2) {
                        $$('ImagePos').hide(); // Hide posterior DNI image if in validation
                        $$('DNPPV').show(); // Show posterior DNI validation in process label
                    } else {
                        if (response[0].dniFrontBack == 3) {
                            $$('ImagePos').hide(); // Hide posterior DNI image if verified
                            $$('DNPV').show(); // Show posterior DNI verified label
                        }
                    }
                    var user = {
                        Id: response[0].Id,
                        Name: response[0].Name,
                        Phone: response[0].Phone,
                        DocNumber: response[0].DocNumber,
                        Address: response[0].Address,
                        Facebook: response[0].Facebook,
                        Instagram: response[0].Instagram,
                        OtherSocialMediaName: response[0].OtherSocialMediaName,
                        OtherSocialMediaContact: response[0].OtherSocialMediaContact,
                        dniFront: response[0].dniFront,
                        dniFrontBack: response[0].dniFrontBack,
                    };
                    $$('form').parse(user); // Populate form with user data
                },
            });
            $$('Other').attachEvent('onChange', function (newv, oldv) {
                if ($$('Other').getValue() == '1') {
                    $$('OtherSocialMediaName').hide(); // Hide fields if 'No' is selected
                    $$('OtherSocialMediaContact').hide();
                } else {
                    $$('OtherSocialMediaName').show(); // Show fields if 'Yes' is selected
                    $$('OtherSocialMediaContact').show();
                }
            });
        });
    }
}

// FILE DOCUMENTED
